// HR department dashboard. Tiles are sourced from GetHRDashboardSummary (single aggregated
// row, CC-scoped server-side by RoleId/UserId) — see spGetHRDashboardSummary.sql. Every tile
// except Active Headcount and the two mini stats drills into a per-employee list
// (spGetHRDashboardDetailList.sql).
import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    Users, FileClock, UserMinus, ShieldAlert, CalendarCheck, UserPlus, Award, Loader2,
} from 'lucide-react';
import FrequentAccessCard from './FrequentAccessCard';
import { MiniStatTile } from './StatTile';
import NeedsAttentionCard from './NeedsAttentionCard';
import DetailListModal from './DetailListModal';
import { getHRDashboardDetailList } from '../../api/HRAPI/hrDashboardAPI';
import {
    fetchHRDashboardSummary,
    selectHRDashboardSummary,
    selectHRDashboardLoading,
} from '../../slices/dashboardSlice/hrDashboardSlice';

// Maps each drillable tile to its detail-list Type, modal title, and the date column's label
const DETAIL_CONFIG = {
    ThisMonthJoinee: { title: 'This Month Joinees', dateColumnLabel: 'Joining Date' },
    OneYearCompletion: { title: '1-Year Anniversary Soon', dateColumnLabel: 'Anniversary Date' },
    ContractExpiring: { title: 'Contracts Expiring', dateColumnLabel: 'Contract End Date' },
    NearingRetirement: { title: 'Nearing Retirement', dateColumnLabel: 'Turns 60 On' },
    NearingPF58: { title: 'Nearing PF Age (58)', dateColumnLabel: 'Turns 58 On' },
    OnLeaveToday: { title: 'On Leave Today', dateColumnLabel: 'Leave Period' },
};

// Rows for the "Needs attention" panel — sorted-by-count list of upcoming employee events
const NEEDS_ATTENTION_ROWS = [
    { key: 'OneYearCompletion', title: '1-Year anniversary', subtitle: 'Completing 1 year of service', icon: Award, tone: 'purple', dueLabel: 'Next 30 days' },
    { key: 'ContractExpiring', title: 'Contracts expiring', subtitle: 'Contract staff', icon: FileClock, tone: 'orange', dueLabel: 'Next 30 days' },
    { key: 'NearingPF58', title: 'Nearing PF age (58)', subtitle: 'Stop PF before 58th birthday', icon: ShieldAlert, tone: 'red', dueLabel: 'Upcoming' },
    { key: 'NearingRetirement', title: 'Nearing retirement', subtitle: 'Turning 60', icon: UserMinus, tone: 'orange', dueLabel: 'Next 90 days' },
];

const HRDashboard = ({ onNavigate, linkFrequency, trackMenuUsage }) => {
    const { roleData, userData } = useSelector((state) => state.auth);
    const dispatch = useDispatch();

    const summary = useSelector(selectHRDashboardSummary);
    const loading = useSelector(selectHRDashboardLoading);

    const userId = userData?.userId || userData?.UID || userData?.employeeId || '';
    const roleId = userData?.roleId || userData?.RID || 0;

    const [detailModal, setDetailModal] = useState({ open: false, loading: false, rows: [], type: null });

    useEffect(() => {
        // roleId/userId drive backend CC-scoping: for roles with ApplicableForCC='Yes',
        // results are restricted to the cost centers assigned to this user in UserCostCenters.
        dispatch(fetchHRDashboardSummary({ roleId, userId }));
    }, [dispatch, roleId, userId]);

    const openDetail = async (type) => {
        setDetailModal({ open: true, loading: true, rows: [], type });
        try {
            const response = await getHRDashboardDetailList({ type, roleId, userId });
            setDetailModal({ open: true, loading: false, rows: response?.Data || [], type });
        } catch (error) {
            setDetailModal({ open: true, loading: false, rows: [], type });
        }
    };

    const closeDetail = () => setDetailModal({ open: false, loading: false, rows: [], type: null });

    const activeConfig = detailModal.type ? DETAIL_CONFIG[detailModal.type] : null;
    const needsAttentionRows = NEEDS_ATTENTION_ROWS.map((row) => ({ ...row, count: summary?.[`${row.key}Count`] || 0 }));

    return (
        <div className="space-y-6">
            {/* Welcome Header */}
            <div className="bg-gradient-to-r from-[#0d1b5e] to-[#0a1240] dark:from-[#0a1240] dark:to-gray-900 rounded-lg p-6 text-white transition-colors">
                <h1 className="text-2xl font-bold mb-2">Welcome Back!</h1>
                <p className="text-orange-200 dark:text-orange-300">Employee: {userData?.firstName} | Role: {userData?.roleCode}</p>
            </div>

            {/* Stats + needs-attention on one side, Quick access on the other */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
                <div className="lg:col-span-2 space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                        {/* Active Headcount — headline tile */}
                        <div className="sm:col-span-2 bg-gradient-to-br from-[#0d1b5e] to-[#16247f] dark:from-[#0a1240] dark:to-[#111a4d] rounded-lg p-5 text-white shadow-md flex flex-col justify-between">
                            <div className="flex items-start justify-between">
                                <p className="text-sm font-medium text-blue-100">Active Headcount</p>
                                <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                                    <Users className="w-5 h-5 text-white" />
                                </div>
                            </div>
                            <div className="mt-3">
                                <p className="text-4xl font-bold">
                                    {loading ? <Loader2 className="w-7 h-7 animate-spin text-white" /> : (summary?.ActiveHeadcount ?? 0)}
                                </p>
                                <p className="text-xs text-blue-200 mt-1">Approved, currently employed</p>
                            </div>
                        </div>

                        <MiniStatTile
                            title="Joinees this month"
                            subtitle="Joined in the current month"
                            icon={UserPlus}
                            loading={loading}
                            value={summary?.ThisMonthJoineeCount}
                            onClick={summary?.ThisMonthJoineeCount > 0 ? () => openDetail('ThisMonthJoinee') : undefined}
                        />
                        <MiniStatTile
                            title="On leave today"
                            subtitle="Approved leave covering today"
                            icon={CalendarCheck}
                            loading={loading}
                            value={summary?.OnLeaveTodayCount}
                            onClick={summary?.OnLeaveTodayCount > 0 ? () => openDetail('OnLeaveToday') : undefined}
                        />
                    </div>

                    {/* Needs attention */}
                    <NeedsAttentionCard
                        subtitle="Upcoming employee events · sorted by count"
                        rows={needsAttentionRows}
                        loading={loading}
                        onOpen={openDetail}
                    />
                </div>

                <div className="lg:col-span-1">
                    <FrequentAccessCard
                        roleData={roleData}
                        linkFrequency={linkFrequency}
                        trackMenuUsage={trackMenuUsage}
                        onNavigate={onNavigate}
                    />
                </div>
            </div>

            <DetailListModal
                open={detailModal.open}
                onClose={closeDetail}
                title={activeConfig?.title || ''}
                dateColumnLabel={activeConfig?.dateColumnLabel || 'Date'}
                loading={detailModal.loading}
                rows={detailModal.rows}
            />
        </div>
    );
};

export default HRDashboard;
